import { AJUDA_PRODUCAO } from "@/lib/ajuda/dialogos/producao";
import { EntregaDoPedidoBloco } from "./EntregaDoPedidoBloco";
import { MenuDeImpressao } from "./MenuDeImpressao";
import { codigoDoPedido } from "@/lib/codigo-do-pedido";
import { useState } from "react";
import { Check, Download, PenLine, RotateCcw, X, XCircle } from "lucide-react";
import { toast } from "sonner";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
  Tabs,
  TabsContent,
  TabsList,
  TabsTrigger,
} from "@bookdental/ui";
import { valorDaTaxaDeUrgencia, type ProsthesisStatus } from "@bookdental/schemas";
import { useTaxaDeUrgencia } from "@/lib/taxa-de-urgencia";
import { EditarServicoDialog } from "./EditarServicoDialog";
import { RequestReworkDialog, type ReworkTarget } from "@/components/prosthesis/RequestReworkDialog";
import { trpc } from "@/lib/trpc";
import { usePermissao } from "@/lib/use-permissao";
import { STATUS_META, STATUS_ORDER, STATUS_TERMINAIS, formatBRL, formatDate } from "@/components/prosthesis/prosthesis-meta";
import { CartaoDoServico } from "@/components/prosthesis/CartaoDoServico";
import { PilulaDeStatus, fraseNormal } from "@/components/prosthesis/PilulaDeStatus";
import { SentChannelsField } from "@/components/prosthesis/SentChannelsField";
import type { ProsthesisServiceRow } from "@/components/prosthesis/types";
import { EntityFilesPanel } from "@/components/files/EntityFilesPanel";
import { ColorSelect } from "./ColorSelect";
import { orderIsPriced, orderTotalCents, type IncomingOrderRow } from "./production-types";
import { useAceitarPedido } from "./use-aceitar-pedido";
import { CancelarServicoDialog } from "./CancelarServicoDialog";
import { DeclineOrderDialog } from "./DeclineOrderDialog";
import { AcrescimosDoPedido } from "@/components/prosthesis/AcrescimosDoPedido";
import { PrazoDoPedido } from "@/components/prosthesis/PrazoDoPedido";
import { AlterarPrazoDialog } from "./AlterarPrazoDialog";

async function downloadAttachment(utils: ReturnType<typeof trpc.useUtils>, fileId: string) {
  try {
    const { url } = await utils.file.getDownloadUrl.fetch({ fileId });
    window.open(url, "_blank", "noopener,noreferrer");
  } catch {
    toast.error("Não foi possível baixar o arquivo.");
  }
}

function ServiceDetailRow({
  service,
  onPropor,
  onRefazer,
}: {
  service: ProsthesisServiceRow;
  onPropor: () => void;
  onRefazer: () => void;
}) {
  const utils = trpc.useUtils();
  const podeGerenciar = usePermissao("production.manage");
  const setStatus = trpc.prosthesis.incoming.setStatus.useMutation({
    onSuccess: () => utils.prosthesis.incoming.list.invalidate(),
    onError: (e) => toast.error(e.message || "Não foi possível mudar o status."),
  });
  // Cancelar pelo seletor passa pelo mesmo diálogo com motivo do Finalizar.
  const [cancelando, setCancelando] = useState(false);

  const meta = STATUS_META[service.status];
  return (
    <>
      <CartaoDoServico
        service={service}
        status={
          podeGerenciar ? (
            // Mesmo desenho da pílula (caixa normal, 12px), só que abre o seletor.
            <ColorSelect
              size="md"
              className="h-6 px-2.5 text-xs font-medium normal-case"
              value={service.status}
              disabled={setStatus.isPending}
              onChange={(v) => (v === "CANCELADO" ? setCancelando(true) : setStatus.mutate({ serviceId: service.id, status: v as ProsthesisStatus }))}
              options={STATUS_ORDER.map((st) => ({
                value: st,
                label: fraseNormal(STATUS_META[st].label),
                bg: STATUS_META[st].bg,
                color: STATUS_META[st].color,
              }))}
            />
          ) : (
            <PilulaDeStatus rotulo={meta.label} cor={meta.color} fundo={meta.bg} alerta={service.hasOpenRework} />
          )
        }
        acoes={
          // Concluído troca Editar por Refazer (editar o entregue reescreveria o passado); cancelado
          // e recusado não têm nenhum dos dois.
          (podeGerenciar && service.status !== "CANCELADO" && service.status !== "RECUSADO") || service.attachmentFile ? (
            <>
              {podeGerenciar && !STATUS_TERMINAIS.includes(service.status) && (
                <button type="button" onClick={() => setCancelando(true)} className="mr-auto inline-flex items-center gap-1.5 rounded-md border border-red-200 px-2.5 py-1 text-xs font-semibold text-red-700 transition-colors hover:bg-red-50 dark:border-red-500/40 dark:text-red-400 dark:hover:bg-red-950/20">
                  <XCircle className="h-3.5 w-3.5" />
                  Cancelar serviço
                </button>
              )}
              {service.attachmentFile && (
                <button type="button" onClick={() => downloadAttachment(utils, service.attachmentFile!.id)} className="inline-flex items-center gap-1.5 rounded-md border border-zinc-200 px-2.5 py-1 text-xs font-semibold text-zinc-700 transition-colors hover:bg-zinc-50 dark:border-border dark:text-foreground dark:hover:bg-muted">
                  <Download className="h-3.5 w-3.5" />
                  Baixar anexo
                </button>
              )}
              {podeGerenciar && service.status === "CONCLUIDO" && (
                <button type="button" onClick={onRefazer} className="inline-flex items-center gap-1.5 rounded-md border border-zinc-200 px-2.5 py-1 text-xs font-semibold text-zinc-700 transition-colors hover:bg-zinc-50 dark:border-border dark:text-foreground dark:hover:bg-muted">
                  <RotateCcw className="h-3.5 w-3.5" />
                  Refazer serviço
                </button>
              )}
              {podeGerenciar && !STATUS_TERMINAIS.includes(service.status) && (
                <button type="button" onClick={onPropor} className="inline-flex items-center gap-1.5 rounded-md border border-zinc-200 px-2.5 py-1 text-xs font-semibold text-zinc-700 transition-colors hover:bg-zinc-50 dark:border-border dark:text-foreground dark:hover:bg-muted">
                  <PenLine className="h-3.5 w-3.5" />
                  Editar serviço
                </button>
              )}
            </>
          ) : undefined
        }
      />
      <CancelarServicoDialog
        aberto={cancelando}
        servico={service.serviceCategory.label}
        pendente={setStatus.isPending}
        onFechar={() => setCancelando(false)}
        onConfirmar={(motivo) =>
          setStatus.mutate({ serviceId: service.id, status: "CANCELADO", motivo }, { onSuccess: () => setCancelando(false) })
        }
      />
    </>
  );
}

export function ProductionOrderDetailDialog({
  order,
  onClose,
}: {
  order: IncomingOrderRow | null;
  onClose: () => void;
}) {
  const utils = trpc.useUtils();
  // A proposta nasce de um SERVIÇO, não do pedido — é nele que a decisão acontece.
  const [servicoAlvo, setServicoAlvo] = useState<any | null>(null);
  const [refazendo, setRefazendo] = useState<ReworkTarget>(null);
  const total = order && orderIsPriced(order) ? orderTotalCents(order) : null;
  const podeLancarAcrescimo = usePermissao("prices.manage");
  const taxaDeUrgencia = useTaxaDeUrgencia();
  // O prazo é do pedido inteiro: só quem vê a produção toda muda (o servidor confere igual).
  const gerenciaProducao = usePermissao("production.manage");
  const veProducaoToda = usePermissao("production.viewAll");
  const podeMudarPrazo = gerenciaProducao && veProducaoToda;
  const [mudandoPrazo, setMudandoPrazo] = useState(false);

  const acceptOrder = useAceitarPedido();

  // Recusar aqui usa o mesmo diálogo da lista (com motivo): o confirm genérico recusava sem dizer o porquê.
  const [recusando, setRecusando] = useState(false);
  const declineOrder = trpc.prosthesis.incoming.declineOrder.useMutation({
    onSuccess: () => {
      toast.success("Pedido recusado.");
      utils.prosthesis.incoming.list.invalidate();
      setRecusando(false);
    },
    onError: (e) => toast.error(e.message || "Erro ao recusar pedido."),
  });

  function handleAccept() {
    if (order) void acceptOrder.aceitar(order);
  }

  function handleDecline() {
    if (order) setRecusando(true);
  }

  return (
    <Dialog open={!!order} onOpenChange={(v) => !v && onClose()}>
      <DialogContent className="flex max-h-[85vh] max-w-2xl flex-col gap-0 overflow-hidden p-0 dark:bg-card">
        {order && (
          <>
            <div className="min-h-0 flex-1 overflow-y-auto p-6">
            <DialogHeader className="text-left">
              {/* pr-8: o X de fechar mora no canto; o Imprimir fica antes dele, nunca por baixo. */}
              <div className="flex flex-wrap items-center gap-x-4 gap-y-2 pr-8">
                <DialogTitle ajuda={AJUDA_PRODUCAO.detalheDoPedido}>{order.patientName}</DialogTitle>
                <span title="Status geral do pedido (calculado a partir dos serviços)">
                  <PilulaDeStatus rotulo={STATUS_META[order.status].label} cor={STATUS_META[order.status].color} fundo={STATUS_META[order.status].bg} />
                </span>
                <div className="ml-auto">
                  <MenuDeImpressao order={order} />
                </div>
              </div>
              <DialogDescription>
                Pedido nº {codigoDoPedido(order)} · {order.organization.name}
              </DialogDescription>
            </DialogHeader>

            <Tabs defaultValue="detalhes">
              <TabsList className="grid h-auto w-full grid-cols-2 bg-zinc-100 p-1 dark:bg-muted">
                <TabsTrigger value="detalhes">Detalhes</TabsTrigger>
                <TabsTrigger value="arquivos">Arquivos</TabsTrigger>
              </TabsList>

              <TabsContent value="detalhes" className="mt-4 space-y-4">
                <div className="grid grid-cols-2 gap-3 rounded-lg bg-zinc-50 p-3 text-sm dark:bg-background/50 sm:grid-cols-4">
                  <div>
                    <p className="text-[11px] uppercase tracking-wide text-zinc-500 dark:text-muted-foreground">Dentista</p>
                    <p className="font-medium text-zinc-900 dark:text-foreground">{order.dentistName ?? "—"}</p>
                  </div>
                  <div>
                    <p className="text-[11px] uppercase tracking-wide text-zinc-500 dark:text-muted-foreground">Data do pedido</p>
                    <p className="font-medium text-zinc-900 dark:text-foreground">{formatDate(order.createdAt)}</p>
                  </div>
                  <PrazoDoPedido
                    deadline={order.deadline}
                    alteracoes={(order as any).alteracoes}
                    onAlterar={podeMudarPrazo && !STATUS_TERMINAIS.includes(order.status as ProsthesisStatus) ? () => setMudandoPrazo(true) : undefined}
                  />
                  <div>
                    <p className="text-[11px] uppercase tracking-wide text-zinc-500 dark:text-muted-foreground">Valor total</p>
                    <p className="font-medium text-zinc-900 dark:text-foreground">{formatBRL(total)}</p>
                  </div>
                </div>

                <EntregaDoPedidoBloco order={order} />

                {order.description && (
                  <div>
                    <p className="mb-1 text-[11px] uppercase tracking-wide text-zinc-500 dark:text-muted-foreground">
                      Observações gerais
                    </p>
                    <p className="text-sm text-zinc-700 dark:text-foreground/80">{order.description}</p>
                  </div>
                )}

                <SentChannelsField orderId={order.id} sentChannels={order.sentChannels} />

                <div>
                  <p className="mb-2 text-[11px] uppercase tracking-wide text-zinc-500 dark:text-muted-foreground">
                    Serviços ({order.services.length})
                  </p>
                  <div className="flex flex-col gap-2">
                    {order.services.map((s) => (
                      <ServiceDetailRow
                        key={s.id}
                        service={s}
                        onPropor={() => setServicoAlvo(s)}
                        onRefazer={() =>
                          setRefazendo({
                            serviceId: s.id,
                            serviceLabel: s.serviceCategory.label,
                            patientName: order.patientName,
                          })
                        }
                      />
                    ))}
                  </div>
                </div>

                <AcrescimosDoPedido
                  orderId={order.id}
                  acrescimos={order.acrescimos ?? []}
                  podeEditar={podeLancarAcrescimo && order.status !== "CANCELADO" && order.status !== "RECUSADO"}
                  taxaDeUrgenciaCents={taxaDeUrgencia ? valorDaTaxaDeUrgencia(taxaDeUrgencia, order.services ?? []) : undefined}
                />

              </TabsContent>

              <TabsContent value="arquivos" className="mt-4">
                <EntityFilesPanel relatedType="PROSTHESIS_ORDER" relatedId={order.id} />
              </TabsContent>
            </Tabs>
            </div>
            {order.status === "AGUARDANDO_CONFIRMACAO" && (
              <div className="flex justify-end gap-2 border-t border-zinc-200 px-6 py-3 dark:border-border">
                <button
                  type="button"
                  disabled={declineOrder.isPending || acceptOrder.isPending}
                  onClick={handleDecline}
                  className="inline-flex h-9 items-center gap-1.5 rounded-md border border-red-200 px-3 text-sm font-semibold text-red-600 transition-colors hover:bg-red-50 disabled:opacity-60 dark:border-red-900/50 dark:text-red-400 dark:hover:bg-red-950/30"
                >
                  <X className="h-4 w-4" />
                  Recusar
                </button>
                <button
                  type="button"
                  disabled={acceptOrder.isPending || declineOrder.isPending}
                  onClick={handleAccept}
                  className="inline-flex h-9 items-center gap-1.5 rounded-md bg-emerald-600 px-3 text-sm font-semibold text-white shadow-sm transition-colors hover:bg-emerald-700 disabled:opacity-60"
                >
                  <Check className="h-4 w-4" />
                  Aceitar
                </button>
              </div>
            )}
          </>
        )}
      </DialogContent>
      {order && (
        <>
          <EditarServicoDialog
            aberto={!!servicoAlvo}
            servico={servicoAlvo}
            onFechar={() => setServicoAlvo(null)}
          />
          <RequestReworkDialog target={refazendo} lado="laboratorio" onClose={() => setRefazendo(null)} />
          <DeclineOrderDialog
            order={recusando ? order : null}
            isPending={declineOrder.isPending}
            onClose={() => setRecusando(false)}
            onConfirm={(reason) => declineOrder.mutate({ orderId: order.id, reason })}
          />
          <AlterarPrazoDialog pedido={mudandoPrazo ? order : null} onFechar={() => setMudandoPrazo(false)} />
        </>
      )}
    </Dialog>
  );
}
